import type { PropsWithChildren } from "react";
import type { FeedAction, FeedActionContext } from "../feedTypes.js";
import type { FeedCardComponentProps } from "../FeedRenderer.js";
import { EditableTitle } from "./EditableTitle.js";

type FeedCardShellProps = PropsWithChildren<FeedCardComponentProps>;

function renderActionButton(
  action: FeedAction,
  onAction: (action: FeedAction, context?: FeedActionContext) => void,
  itemId: string
) {
  return (
    <button
      key={action.id}
      type="button"
      className="feed-card-action-button"
      onClick={() => onAction(action, { itemId, source: "header" })}
    >
      {action.icon && <span className="feed-card-action-icon" aria-hidden>{action.icon}</span>}
      <span>{action.label ?? action.id}</span>
    </button>
  );
}

export function FeedCardShell({
  item,
  data,
  loading,
  error,
  children,
  onAction,
  requestReload,
  onTitleCommandExecute,
  onTitleFilter
}: FeedCardShellProps) {
  // Determine column span from layout config
  const columnSpan = item.layout?.columnSpan;
  const dataColumnSpan = typeof columnSpan === "number" ? columnSpan.toString() : columnSpan;
  
  return (
    <section 
      className="feed-card" 
      aria-labelledby={`${item.id}-title`}
      data-column-span={dataColumnSpan}
    >
      <header className="feed-card-header">
        <div className="feed-card-header-text">
          <EditableTitle 
            title={item.title || ""} 
            id={`${item.id}-title`}
            onTitleChange={(newTitle) => {
              // Update item title if needed
              if (item.title !== newTitle) {
                item.title = newTitle;
              }
            }}
            onCommandExecute={onTitleCommandExecute ? (command) => onTitleCommandExecute(item.id, command) : undefined}
            onFilter={onTitleFilter ? (filterText) => onTitleFilter(item.id, filterText) : undefined}
          />
          {item.description && <p className="feed-card-description">{item.description}</p>}
        </div>
        {item.actions && item.actions.length > 0 && (
          <div className="feed-card-actions" role="group">
            {item.actions.map((action) => renderActionButton(action, onAction, item.id))}
          </div>
        )}
      </header>
      <div className="feed-card-body">
        {loading && data === undefined ? <div className="feed-card-loading">Lade Daten…</div> : children}
        {error && (
          <div className="feed-card-error" role="alert">
            <span className="feed-card-error-icon" aria-hidden>
              ⚠️
            </span>
            <div>
              <strong>Fehler beim Laden</strong>
              <p>{error}</p>
            </div>
            <button
              type="button"
              className="feed-card-retry"
              onClick={() => {
                if (item.dataSource) {
                  requestReload();
                }
              }}
            >
              Erneut versuchen
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
